import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, TransferState, Type, makeStateKey } from '@angular/core';
import * as fromRoot from '@app/reducers';
import { getLanguageWithFallback } from '@app/shared/helpers/language';
import { computePendingRolesAndPoliciesUpdates } from '@app/shared/helpers/permissions';
import { resolveCountries } from '@app/shared/helpers/vat';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { loadStripe } from '@stripe/stripe-js/pure';
import { DeviceDetectorService } from 'ngx-device-detector';
import { concat, defer, EMPTY, forkJoin, from, fromEvent, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { environment } from 'src/environments/environment';
import { DomainActions, EnvironmentActions } from '../actions';
import { CookiesService } from '../services/cookies.service';
import { IamService } from '../services/iam.service';
import { RedirectionService } from '../services/redirection.service';
import { ScitylanaService } from '../services/scitylana.service';
import { TokenService } from '../services/token.service';

import { Router } from '@angular/router';
import { setConfigHosts, setRegion } from '@app/shared/helpers/config';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Language } from '@app/shared/types/Language';
import { Region } from '@smash-sdk/core';
import { Discovery } from '@smash-sdk/discovery/ejs/02-2023/discovery';
import ListPublicServicesError from '@smash-sdk/discovery/ejs/02-2023/types/ListPublicServices/ListPublicServicesError';
import { Vat } from '@smash-sdk/vat/ejs/10-2019/vat';
import { GoogleTagManagerService } from '../lib/google-tag-manager/angular-google-tag-manager.service';
import { hostnameIsFromMainDomain } from '@app/shared/helpers/location';
import { DomainService } from '../services/domain.service';
import { DialogService } from '@ngneat/dialog';
import { ModalLanguageLoadErrorComponent } from '@app/shared/components/modal-language-load-error/modal-language-load-error.component';
import { ModalChunkLoadErrorComponent } from '@app/shared/components/modal-chunk-load-error/modal-chunk-load-error.component';

export const DISCOVERY_SERVICES_KEY = makeStateKey('discovery-services');
export const VAT_COUNTRIES_KEY = makeStateKey('vat-countries');

@Injectable()
export class EnvironmentEffects {

  constructor(
    private actions$: Actions,
    private tokenService: TokenService,
    private translate: TranslateService,
    private store: Store<fromRoot.State>,
    private deviceService: DeviceDetectorService,
    private iamService: IamService,
    private cookiesService: CookiesService,
    private scitylanaService: ScitylanaService,
    private router: Router,
    private redirectionService: RedirectionService,
    private state: TransferState,
    private googleTagManagerService: GoogleTagManagerService,
    private domainService: DomainService,
    private dialog: DialogService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {

  }

  // When a language switch fails (typically the translation file could not be loaded on a
  // connection loss), tell the user rather than failing silently. Fires on EVERY language change
  // (not only the uploader). Browser-only: DialogService must never run during SSR.
  showLanguageLoadError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.SelectLanguageFailure),
      tap(() => {
        if (isPlatformBrowser(this.platformId)) {
          // Explicit stable id: @ngneat/dialog dedups by the component CLASS NAME, which minification
          // mangles - two error modals could otherwise get the same mangled id and stack over each other.
          this.openOfflineModal(ModalLanguageLoadErrorComponent, 'language-load-error');
        }
      }),
    ),
    { dispatch: false },
  );

  // A lazy-route chunk failed to load (offline / stale bundle) - surface the generic offline modal.
  // Browser-gated (SSR never opens a dialog); stable id for the same anti-stacking reason as above.
  showChunkLoadError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.ChunkLoadFailure),
      tap(() => {
        if (isPlatformBrowser(this.platformId)) {
          this.openOfflineModal(ModalChunkLoadErrorComponent, 'chunk-load-error');
        }
      }),
    ),
    { dispatch: false },
  );

  // On reconnect, COMPLETE a language switch that failed offline: re-navigate to its URL (re-drives
  // the chunk load + SwitchLanguageGuard + translation fetch, so the language AND the URL end correct),
  // or re-dispatch SelectLanguage for a non-navigation switch (en / viewer, URL already unprefixed).
  // Cleared immediately after firing so a flapping network cannot re-navigate twice; if the re-attempt
  // fails again the same failure action re-captures the target, re-arming for the next 'online'.
  // Browser-only (window / SSR). Uses the same 'online' signal as the modal auto-close, so the retry
  // fires exactly when the modal dismisses.
  reconnectPendingLanguageSwitch$ = createEffect(() =>
    // The `.pipe(...)` is OUTSIDE the ternary so createEffect always wraps a FRESH observable: returning
    // the shared EMPTY singleton directly makes NgRx fail to re-mark it on a second instance (test
    // re-injection) with "Cannot redefine property __@ngrx/effects_create__". On the server the ternary
    // yields EMPTY (window never touched); on the browser it listens to the 'online' event.
    (isPlatformBrowser(this.platformId) ? fromEvent(window, 'online') : EMPTY).pipe(
      withLatestFrom(this.store.select(fromRoot.getPendingLanguageSwitch)),
      filter(([, pending]) => !!pending && (!!pending.url || !!pending.language)),
      mergeMap(([, pending]) => {
        // A `language` on the pending target means the chunk was CACHED and only the {lang}.json fetch
        // failed (SelectLanguageFailure): re-navigating re-fetches the json — no reload needed.
        if (pending.language) {
          if (pending.url) {
            // navigateByUrl returns a promise that REJECTS if the re-navigation fails (e.g. the chunk
            // still 404s). The failure is already owned by the event stream — ChunkLoadErrorHandler /
            // NavigationError re-dispatch a *Failure action that re-arms pending — so swallow the
            // rejection here to avoid an unhandled promise rejection surfacing to the global handler.
            this.router.navigateByUrl(pending.url).catch(() => undefined);
          } else {
            // No route was involved (en / viewer dispatch sites): just re-apply the translations.
            return [
              EnvironmentActions.SelectLanguage({ language: pending.language }),
              EnvironmentActions.ClearPendingLanguageSwitch(),
            ];
          }
          return [EnvironmentActions.ClearPendingLanguageSwitch()];
        }
        // Otherwise the ROUTE CHUNK itself failed (ChunkLoadFailure -> url only). EMPIRICALLY (a RECONNECT
        // e2e that used router.navigateByUrl here) re-navigating back online still rejects with
        // ChunkLoadError - a router navigation does not re-fetch the failed chunk; only a full document
        // load gets a runtime that fetches it. So reload to the target URL, which lands the user on the
        // route in the language they asked for. (Trade-off: a full reload loses in-flight state e.g. an
        // upload - accepted, it is the only path that actually completes the switch.)
        if (pending.url) {
          this.reloadTo(pending.url);
        }
        return [EnvironmentActions.ClearPendingLanguageSwitch()];
      }),
    ),
  );

  /**
   * Full document load to `url` — the ONLY way to recover a language-route chunk that failed offline
   * (webpack memoises the rejected chunk promise, so a router navigation re-uses the failure). Isolated
   * in a method so a test can assert the reconnect effect takes this path (window.location is otherwise
   * unspyable in jsdom).
   */
  private reloadTo(url: string): void {
    window.location.href = url;
  }

  /**
   * Opens an offline-error modal and, when it closes for ANY reason other than the reconnect
   * auto-close (the components close with the 'online' reason), forgets the pending language switch.
   * This covers dismissal by the button, ESC, and backdrop (@ngneat/dialog allows all three) - so a
   * modal the user deliberately dismissed never ghost-navigates on a later reconnect.
   */
  private openOfflineModal(component: Type<unknown>, id: string): void {
    const ref = this.dialog.open(component, { id });
    // @ngneat/dialog dedups by id: open() returns undefined (opens nothing) when a modal with this id
    // is already on screen. Its .d.ts lies (declares DialogRef), so guard explicitly - dereferencing
    // afterClosed$ on undefined would throw a TypeError out of the effect, and after NgRx's resubscribe
    // budget the whole offline-modal effect dies for the session. The existing modal stays open (its
    // own afterClosed$ subscription is intact), so skipping here is a correct no-op.
    if (!ref) {
      return;
    }
    ref.afterClosed$.pipe(take(1)).subscribe((reason) => {
      if (reason !== EnvironmentActions.OFFLINE_MODAL_RECONNECT_CLOSE_REASON) {
        this.store.dispatch(EnvironmentActions.ClearPendingLanguageSwitch());
      }
    });
  }
  loadDeviceInfos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadDevice),
      switchMap(() => {
        const device = {
          deviceInfo: this.deviceService.getDeviceInfo(),
          isMobile: this.deviceService.isMobile(),
          isDesktop: this.deviceService.isDesktop(),
          isTablet: this.deviceService.isTablet()
        };
        return [EnvironmentActions.loadDeviceSuccess({ device })];
      })
    ));

  // Action to retrieve Services from current Domain region
  loadServicesFromDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadServicesFromDomain),
      withLatestFrom(
        this.store.select(fromRoot.getCurrentDomain),
      ),
      switchMap(([_, domain]) => {
        if (domain?.region) {
          const discoverySdk = new Discovery({ region: domain.region as Region });
          return defer(() => discoverySdk.listPublicServices()).pipe(
            genericRetry({
              retryConfig: [
                ...Constant.NetworkErrorsRetryConfiguration,
                { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
              ]
            }),
            concatMap(({ country, countryCode }) => {
              return [
                EnvironmentActions.loadServicesSuccess({ region: domain.region, country, countryCode }),
              ];
            }),
            catchError((error: HttpErrorResponse) => {
              this.router.navigateByUrl(Constant.redirection.serviceUnavailable);
              throw error; // important to throw error so it can bubble up
            }));
        } else {
          // SSR Loading default service
          const servicesFromState: any = this.state.get(DISCOVERY_SERVICES_KEY, null);
          if (servicesFromState) {
            // to do handle transfer status
            return of(EnvironmentActions.loadServicesSuccess({ ...servicesFromState }));
          } else {
            const discoverySdk = new Discovery({ host: environment.discovery });
            return defer(() => discoverySdk.listPublicServices()).pipe(
              genericRetry({
                retryConfig: [
                  ...Constant.NetworkErrorsRetryConfiguration,
                  { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                  { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                  { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                ]
              }),
              concatMap(({ region, country, countryCode }) => {
                this.state.set(DISCOVERY_SERVICES_KEY, { region, country, countryCode } as any);
                return [
                  EnvironmentActions.loadServicesSuccess({ region, country, countryCode }),
                ];
              }),
              catchError((error: HttpErrorResponse) => {
                this.router.navigateByUrl(Constant.redirection.serviceUnavailable);
                throw error; // important to throw error so it can bubble up
              }));
          }

        }
      })
    ));
  // Action to retrieve services from token region
  loadServicesFromToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadServicesFromToken),
      withLatestFrom(
        this.store.select(fromRoot.getActiveIdentity),
      ),
      switchMap(([_, identity]) => {
        const { region } = identity;
        const discoverySdk = new Discovery({ region: region as Region });
        return defer(() => discoverySdk.listPublicServices()).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
              { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          concatMap(({ country, countryCode }) => {
            return [EnvironmentActions.loadServicesSuccess({ region, country, countryCode })];
          }));
      })
    ));

  loadServicesFromRegion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadServicesFromRegion),
      switchMap((action) => {
        const { region } = action;
        const discoverySdk = new Discovery({ region: region as Region });
        return defer(() => discoverySdk.listPublicServices()).pipe(
          genericRetry({
            retryConfig: [
              ...Constant.NetworkErrorsRetryConfiguration,
              { error: ListPublicServicesError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
              { error: ListPublicServicesError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
              { error: ListPublicServicesError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
            ]
          }),
          concatMap(({ country, countryCode }) => {
            return [EnvironmentActions.loadServicesSuccess({ region, country, countryCode })];
          }));
      })
    ));

  loadServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadServices),
      withLatestFrom(
        this.store.select(fromRoot.getActiveIdentity),
        this.store.select(fromRoot.getCurrentDomainLoaded)
      ),
      switchMap(([action, identity, domainLoaded]: any) => {
        if (action.region) {
          return [EnvironmentActions.loadServicesFromRegion({ region: action.region })];
        } else if (!!identity?.username) {
          return [EnvironmentActions.loadServicesFromToken()];
        } else {

          if (!domainLoaded) {
            const detectedDomain = this.domainService.detectDomain();
            return concat(
              of(DomainActions.loadDomain({ id: detectedDomain })),
              this.store.select(fromRoot.getCurrentDomainLoaded).pipe(
                filter(loaded => loaded),
                take(1),
                map(() => EnvironmentActions.loadServicesFromDomain())
              )
            );
          }

          return [EnvironmentActions.loadServicesFromDomain()];
        }
      })
    )
  );

  loadServicesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadServicesSuccess),
      tap((action) => {
        setConfigHosts(environment.services);
        setRegion(action.region as Region);
        console.log('Region: ', action.region);
        if (isPlatformBrowser(this.platformId)) {
          this.googleTagManagerService.pushTag({
            smash_country_code: action.countryCode,
            smash_country: action.country,
            smash_region: action.region,
          });
        }
      })
    ), { dispatch: false }
  );


  selectLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.SelectLanguage),
      switchMap(({ language, currentPath }) =>
        // defer() so the ngx-translate calls run at subscribe time INSIDE this observable: the SSR
        // TranslateFSLoader does readFileSync/JSON.parse in its method body and ngx-translate invokes
        // it synchronously, so use()/reloadLang()/resetLang() can throw synchronously. Without defer
        // that throw escapes before the catchError below exists, errors the effect stream, and leaves
        // SwitchLanguageGuard's race() with neither Success nor Failure -> a hung SSR navigation. With
        // defer the sync throw becomes an error notification the catchError maps to SelectLanguageFailure.
        defer(() => {
          const notLoaded = this.translate.translations[language] === undefined;
          // ngx-translate caches a FAILED load forever (shareReplay) AND use() short-circuits when the
          // target equals the optimistically-set currentLang -> it would return of(undefined) with no
          // fetch (a bogus success, UI left in raw keys). reloadLang() forces a real re-fetch in that
          // currentLang case; otherwise resetLang() drops any poisoned failed-load cache so use()
          // re-fetches. A healthy, already-loaded language takes use()'s fast path untouched.
          if (notLoaded && this.translate.currentLang === language) {
            // reloadLang() -> getTranslation() re-fetches but emits NEITHER onLangChange NOR
            // onTranslationChange, so the already-rendered translate pipes would keep showing raw keys
            // despite the successful load. Re-publish the fetched translations via setTranslation, the
            // only public method that emits onTranslationChange (the pipe is subscribed to it), so the
            // UI refreshes. Same shareReplay'd request - no extra network round-trip.
            return this.translate.reloadLang(language).pipe(
              tap((translations) => this.translate.setTranslation(language, translations)),
            );
          }
          if (notLoaded) {
            this.translate.resetLang(language);
          }
          return this.translate.use(language);
        }).pipe(
          map(() => {
            // Cookie only on success: a failed switch must not leave the cookie naming a language
            // the UI is not actually showing.
            this.cookiesService.setCookie('lang', language);
            return EnvironmentActions.SelectLanguageSuccess({ language });
          }),
          // catchError INSIDE switchMap: keeps the effect alive (an outer catchError would end the
          // whole effect after the first failure) and surfaces the failure for the offline modal. It
          // now also catches a synchronous loader throw hoisted into the stream by defer() above.
          catchError(() => {
            this.translate.resetLang(language); // drop the poisoned cache so a re-click can retry
            // Carry the target URL so a reconnect can re-navigate here and complete the switch.
            return of(EnvironmentActions.SelectLanguageFailure({ language, url: currentPath }));
          }),
        )
      )
    ),
  );

  setupLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.SetupLanguage),
      withLatestFrom(
        this.store.pipe(select(fromRoot.getSelectedLanguage)),
      ),
      switchMap(([action, selectedLanguage]) => {
        // this is not executed anymore serverside. since Appcomponent is only bootstraped browserside
        // defer() guards against a synchronous loader throw the same way as selectLanguage$ above.
        if (selectedLanguage) {
          return defer(() => this.translate.use(selectedLanguage)).pipe(
            map(() => EnvironmentActions.SetupLanguageSuccess({ language: selectedLanguage })),
            // Keep the effect alive on a boot-time load failure (do not burn NgRx's crash budget);
            // no modal at bootstrap. resetLang so a later switch can re-fetch.
            catchError(() => { this.translate.resetLang(selectedLanguage); return EMPTY; }),
          );
        } else {
          const browserLang = this.translate.getBrowserLang();
          const targetLang: Language = getLanguageWithFallback(browserLang);
          return defer(() => this.translate.use(targetLang)).pipe(
            map(() => {
              this.cookiesService.setCookie('lang', targetLang); // cookie only on a successful load
              return EnvironmentActions.SetupLanguageSuccess({ language: targetLang });
            }),
            catchError(() => { this.translate.resetLang(targetLang); return EMPTY; }),
          );
        }
      })
    )
  );

  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.loadCountries),
      switchMap(() => {
        // SSR Loading default countries
        const countriesFromState: any = this.state.get(VAT_COUNTRIES_KEY, null);
        if (countriesFromState) {
          // to do handle transfer status
          return of(EnvironmentActions.loadCountriesSuccess({ ...countriesFromState }));
        } else {
          const vatSdk = new Vat();
          return from(vatSdk.listCountries()).pipe(
            map(({ countries }) => {
              const resolvedCountries = resolveCountries(countries);
              this.state.set(VAT_COUNTRIES_KEY, { countries: resolvedCountries } as any);
              return EnvironmentActions.loadCountriesSuccess({ countries: resolvedCountries });
            }),
            catchError((error: any) => of(EnvironmentActions.loadCountriesFailure({ error: error.message })))
          );
        }

      })
    )
  );

  followDomainRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.FollowDomainRedirect),
      tap((action) => {
        const { protocol, port } = window.location;
        window.location.href = `${protocol}//${action.redirect}${port ? ':' + port : ''}`;
      })
    ),
    { dispatch: false }
  );

  redirectToSelectedDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.redirectToSelectedDomain),
      withLatestFrom(
        this.store.pipe(select(fromRoot.getCurrentDomain)),
        this.store.pipe(select(fromRoot.getActiveIdentity)),
      ),
      tap(([action, currentDomain, identity]) => {
        const cookieLastActiveTeam = this.cookiesService.getCookie('lastActiveTeam');
        if (cookieLastActiveTeam?.team !== currentDomain?.team) {
          const { protocol, port } = window.location;
          const { account, id, language, refreshToken, region, token, username } = identity;

          try {
            const encodedIdentity = encodeURIComponent(btoa(JSON.stringify({ identity: { account, id, language, refreshToken, region, token, username } })));
            window.location.href = `${protocol}//${cookieLastActiveTeam.domain}${port ? ':' + port : ''}?auth=${encodedIdentity}`;
          } catch (error) {
            throw error;
          }
        } else {
          this.router.navigateByUrl('/');
        }
      })
    ),
    { dispatch: false }
  );

  redirectToAccountWithSelectedDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.redirectToAccountWithSelectedDomain),
      withLatestFrom(
        this.store.pipe(select(fromRoot.getActiveTeam)),
      ),
      tap(([action, selectedTeam]) => {
        const { protocol, port } = window.location;
        window.location.href = `${protocol}//${selectedTeam.domain}${port ? ':' + port : ''}/account/team/${selectedTeam.id}/transfers/sent`;
      })
    ),
    { dispatch: false }
  );

  scanUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.ScanUpdates),
      concatMap((action: any) => {
        return [ // add tasks here
          EnvironmentActions.GetRemotePoliciesAndRoles()
        ];
      })
    ));

  runUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.RunUpdates),
      withLatestFrom(
        this.store.select(fromRoot.getPendingUpdates)
      ),
      concatMap(([action, Updates]: any) => {
        const actions: Action[] = Updates.map(updateAction => updateAction.action);
        if (actions.length) {
          return actions;
        }
        return [{ type: 'RunUpdates noop' }];
      })
    ));

  finalizeUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.FinalizeUpdates),
      withLatestFrom(
        this.store.select(fromRoot.getPendingUpdates)
      ),
      concatMap(([action, Updates]: any) => {
        const actions: Action[] = Updates.map(updateAction => updateAction.action);
        if (!actions.length) {
          return [EnvironmentActions.RunUpdatesSuccess({ refresh: true })];
        }
        return [{ type: 'FinalizeUpdates noop' }];
      })
    ));

  runUpdatesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.RunUpdatesSuccess),
      tap((action: any) => {
        if (action.refresh) {
          window.location.reload();
        }
      })
    ), { dispatch: false });

  getRemotePoliciesAndRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.GetRemotePoliciesAndRoles),
      concatMap((action) => {
        return forkJoin([this.iamService.listPolicies(), this.iamService.listHydratedRoles()]).pipe(
          mergeMap(([{ policies }, roles]: any) => {
            const filteredPolicies = policies.filter(policy => !policy?.name?.startsWith('Managed') && (!policy?.tags?.applicationID || policy?.tags?.applicationID === Constant.resourceTagValues['applicationID']));
            const filteredRoles = roles.filter(role => !role?.name?.startsWith('Managed') && (!role?.tags?.applicationID || role?.tags?.applicationID === Constant.resourceTagValues['applicationID']));
            return [EnvironmentActions.GetRemotePoliciesAndRolesSuccess({ remotePolicies: filteredPolicies, remoteRoles: filteredRoles })];
          })
        );
      }),
      catchError((error: any) => {
        return of(EnvironmentActions.GetRemotePoliciesAndRolesFailure({ error: error.message }))
      })
    ));

  getRemotePoliciesAndRolesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.GetRemotePoliciesAndRolesSuccess),
      withLatestFrom(
        this.store.select(fromRoot.getActiveIdentity),
        this.store.select(fromRoot.getUserRolesAndTeamsLoaded),
      ),
      // boundary: pre-existing pattern in this effect (action carries the untyped
      // GetRemotePoliciesAndRolesSuccess payload); no narrower shared type exists for this tuple.
      concatMap(([action, activeIdentity, userRolesAndTeamsLoaded]: any) => {
        // Guard: userTeams is only safe to diff against the remote policies/roles once it has
        // actually been freshly (re)loaded. ScanUpdates() (the only current trigger for this effect)
        // is dispatched from LayoutComponent's constructor, gated on isAdministrator AND on there
        // being no pending SkipNextScanUpdates (see LayoutComponent and domain.effects.ts's
        // initiateTeamSuccess$) -- but isAdministrator/userTeams only load together INCIDENTALLY
        // (account.reducer.ts's LoadUserRolesAndTeamsSuccess happens to set both in the same case),
        // so this guard stays independently necessary. Without it, a future second ScanUpdates()
        // trigger not gated the same way would run this comparison against an empty/stale userTeams
        // and treat EVERY existing remote policy/role as orphaned, scheduling them all for deletion
        // -- a worse version of the vulnerability domain.effects.ts's initiateTeamSuccess$ fix closed
        // (that one only put the new team's own policies/roles at risk).
        if (!userRolesAndTeamsLoaded) {
          return of({ type: 'ComputePolicies noop -- userTeams not freshly loaded' });
        }

        const { remotePolicies, remoteRoles } = action;
        const account = this.tokenService.getAccountFromToken(activeIdentity.token.token);

        // Diff against EVERY team on the account (iamService.listTeams(), account-wide) rather than
        // fromRoot.getUserTeams (the signed-in user's OWN teams): a role/policy belonging to a team
        // the current user simply isn't personally a member of is not "orphaned" -- only a
        // role/policy matching no team on the account at all is safe to queue for deletion. See
        // docs/task-reports/2026-09-18-stuck-smash-update-popup-investigation.md (root cause 2).
        return this.iamService.listTeams().pipe(
          map(({ teams }) => {
            // boundary: pre-existing pattern (this effect's original, unmodified return type) --
            // computePendingRolesAndPoliciesUpdates has no shared type for its `tasks`/`action`
            // shape, and `.action` is assigned onto the plain object below.
            const updateObj: any = computePendingRolesAndPoliciesUpdates({ remotePolicies, remoteRoles }, { account, teams, region: activeIdentity.region });
            if (updateObj.status === 'need update') {
              updateObj.action = EnvironmentActions.ExecutePoliciesUpdate({ ...updateObj.tasks });
              return EnvironmentActions.ComputePoliciesSuccess({ update: updateObj });
            }

            return { type: 'ComputePolicies noop' };
          }),
          // This call used to have no catchError at all, so a failing listTeams() (e.g. a transient
          // network blip or an expired token) errored this whole effect UNCAUGHT. @ngrx/effects
          // v17's default error handler resubscribes an effect whose observable errors uncaught, but
          // only 10 times for the page's entire lifetime -- the 11th failure permanently kills
          // getRemotePoliciesAndRolesSuccess$ until a full reload, silently breaking every later
          // ScanUpdates() scan. Nested here (not on the outer actions$ pipe) so THIS effect's own
          // subscription survives a single failed scan and can still react to a later ScanUpdates()
          // trigger -- same reasoning as executePoliciesUpdate$'s own catchError below.
          catchError(() => of({ type: 'ComputePolicies noop -- listTeams failed' })),
        );
      })
    ));

  executePoliciesUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.ExecutePoliciesUpdate),
      withLatestFrom(
        this.store.select(fromRoot.getPendingUpdates)
      ),
      concatMap(([action, pendingUpdates]: any) => {
        const { PoliciesToCreate, PoliciesToUpdate, RolesToDelete, RolesToCreate, RolesToUpdate, PoliciesToDelete } = action;
        return this.iamService.executePolicyRoleUpdate({ PoliciesToCreate, PoliciesToUpdate, RolesToDelete, RolesToCreate, RolesToUpdate, PoliciesToDelete }).pipe(
          concatMap((res: any) => {
            const updatesToRun = pendingUpdates.find(update => update.status === 'need update' && update.name === 'Update Policies & Roles');
            return [EnvironmentActions.ExecutePoliciesUpdateSuccess({ update: updatesToRun }), EnvironmentActions.FinalizeUpdates()];
          }),
          // A failed create/update/delete batch (e.g. a 403 on a role/policy DELETE -- very
          // plausible given the account-wide-teams fix above) used to error this whole effect
          // uncaught, so RunUpdatesFailure -- despite existing and being handled correctly by the
          // reducer -- was never dispatched from anywhere, leaving updateRunning stuck true and
          // ModalUpdateComponent's spinner spinning forever. Nested here (not on the outer actions$
          // pipe, unlike the sibling getRemotePoliciesAndRoles$ effect above) so THIS effect's own
          // subscription survives a failure: an outer catchError would complete it on the first
          // error, and a user retrying (re-clicking confirm without reloading the page) would
          // dispatch a second ExecutePoliciesUpdate into an effect that had already stopped
          // listening. See docs/task-reports/2026-09-18-stuck-smash-update-popup-investigation.md
          // (root cause 1).
          catchError((error: any) => of(EnvironmentActions.RunUpdatesFailure()))
        );
      })
    ));

  acceptCGU$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.AcceptCGU),
      map(() => {
        const cgu = {
          cguLastConsentUpdated: new Date().toISOString(),
          cguLastVersion: environment.cgu.version,
          cguLastConsentValue: true,
        };
        this.cookiesService.setCookie('cgu', cgu);
        return EnvironmentActions.AcceptCGUSuccess({ cgu });
      })
    ));

  onBoarding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.OnBoarding),
      withLatestFrom(
        this.store.select(fromRoot.getSelectedLanguage),
      ),
      tap(([action, language]) => {
        const { onBoardingState, redirectToPricing } = action;
        this.cookiesService.setCookie('onBoarding', onBoardingState);
        this.store.dispatch(EnvironmentActions.OnBoardingSuccess({ onBoardingState }));
        if (redirectToPricing) {
          this.redirectionService.redirectTo(redirectToPricing);
        }
      })
    ),
    { dispatch: false });

  loadCookiesConsentConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.LoadCookiesConsentConfiguration),
      map(() => {
        const cookies = this.cookiesService.getAllCookies();
        const cookiesConsentSetByUser = cookies._sucs;

        if (!cookiesConsentSetByUser) {
          this.cookiesService.removeAllCookiesExceptSystemAndCloufront();
        }

        const cookiesConsentConfiguration = this.cookiesService.getCookiesConsentConfiguration();

        if (!cookiesConsentConfiguration.groups.social.enabled) {
          this.cookiesService.cleanCookiesConsentGroupAssociatedCookies('social');
        }
        if (!cookiesConsentConfiguration.groups.analytics.enabled) {
          this.cookiesService.cleanCookiesConsentGroupAssociatedCookies('analytics');
        }

        this.googleTagManagerService.pushTag({ event: Constant.eventGTM.CONSENT_UPDATE });
        return EnvironmentActions.LoadCookiesConsentConfigurationSuccess({ cookiesConsentConfiguration, cookiesConsentSetByUser });
      })
    ));

  acceptAllCookies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.AcceptAllCookies),
      map(() => {
        this.googleTagManagerService.pushTag({ event: Constant.eventGTM.CONSENT_UPDATE });
        return EnvironmentActions.UpdateCookiesConsentConfiguration({
          cookiesConsentConfiguration: {
            groups: {
              analytics: { enabled: true },
              social: { enabled: true },
            }
          }
        });
      })
    ));

  denyAllCookies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.DenyAllCookies),
      map(() => {
        this.googleTagManagerService.pushTag({ event: Constant.eventGTM.CONSENT_UPDATE });
        const isMainDomain = hostnameIsFromMainDomain(location.hostname);
        return EnvironmentActions.UpdateCookiesConsentConfiguration({
          cookiesConsentConfiguration: {
            groups: {
              analytics: { enabled: isMainDomain ? true : false },
              social: { enabled: false },
            }
          }
        });
      })
    ));


  updateCookiesConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.UpdateCookiesConsentConfiguration),
      withLatestFrom(this.store.select(fromRoot.getCookiesConsentConfiguration)),
      map(([action, oldCookiesConsentConfiguration]) => {
        const { cookiesConsentConfiguration } = action;
        this.cookiesService.setCookie('userConsentSet', true);
        if (cookiesConsentConfiguration.groups.social.enabled) {
          this.cookiesService.acceptCookiesConsentGroup('social');
        } else {
          this.cookiesService.denyCookiesConsentGroup('social');
        }
        if (cookiesConsentConfiguration.groups.analytics.enabled) {
          this.cookiesService.acceptCookiesConsentGroup('analytics');
        } else {
          this.cookiesService.denyCookiesConsentGroup('analytics');
        }
        this.googleTagManagerService.pushTag({ event: Constant.eventGTM.CONSENT_UPDATE });
        return EnvironmentActions.UpdateCookiesConsentConfigurationSuccess({ cookiesConsentConfiguration, oldCookiesConsentConfiguration });
      })
    ));

  updateCookiesConfigurationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.UpdateCookiesConsentConfigurationSuccess),
      tap((action) => {
        // If a cookie switch to 'enabled' = false, reload page in order to remove everything associated to this cookie
        const { oldCookiesConsentConfiguration, cookiesConsentConfiguration } = action;
        let newCookiesDisabled = false;
        Object.entries(oldCookiesConsentConfiguration.groups).forEach(([key, value]) => {
          if (value.enabled && !cookiesConsentConfiguration.groups[key].enabled) {
            newCookiesDisabled = true;
          }
        });
        if (newCookiesDisabled) {
          window.location.reload();
        }
      })
    ), { dispatch: false });

  loadStripe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.LoadStripe),
      concatMap(() => {
        return from(loadStripe(environment.stripeKey)).pipe(map(() => EnvironmentActions.LoadStripeSuccess()));
      }),
      catchError(error => of(EnvironmentActions.LoadStripeFailure({ error })))
    ));

  createEventScitylana$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnvironmentActions.CreateEventScitylana),
      concatMap((action) => {
        return this.scitylanaService.postTneveOnScitylana(action.event).pipe(map(() => {
          return EnvironmentActions.CreateEventScitylanaSuccess();
        }));
      }),
      catchError(error => of(EnvironmentActions.CreateEventScitylanaFailure({ error: error.message })))
    ));
}
